import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { useProfileState } from '../../auth/useProfile'

type Profile = components['schemas']['UserResponse']

/** The setting's read side: `learn_from_edits` off the current-user query. */
export function useLearnFromEditsState(): AsyncState<boolean> {
  return useProfileState(
    (p) => p.learn_from_edits,
    'We couldn’t load your preferences. Check your connection and try again.',
  )
}

/**
 * `PATCH /users/me` with `learn_from_edits` and NOTHING else — an omitted key
 * leaves the rest alone and an explicit `null` is a 422, so the body is always
 * a boolean. Turning it OFF also makes the server forget everything it learned.
 *
 * Optimistic (the switch is a single boolean the user is looking at), rolled
 * back (that one field) on any failure, and the user query is invalidated either
 * way so the cache converges on the server's value. It rejects, so a confirm
 * dialog can show the failure and stay open.
 */
export function useSetLearnFromEdits() {
  const client = useQueryClient()
  return useMutation<void, Error, boolean, { previous: Profile | undefined }>({
    networkMode: 'always',
    mutationFn: async (next) => {
      const { error } = await api.PATCH('/api/v1/users/me', { body: { learn_from_edits: next } })
      if (error !== undefined) throw new Error('Could not save the setting.')
    },
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: keys.user.me() })
      const previous = client.getQueryData<Profile>(keys.user.me())
      if (previous) client.setQueryData<Profile>(keys.user.me(), { ...previous, learn_from_edits: next })
      return { previous }
    },
    onError: (_error, _next, context) => {
      // Restore only this field: a whole-snapshot restore would drop another
      // in-flight write to the same cache entry (reporting currency, display name).
      if (context?.previous)
        client.setQueryData<Profile>(keys.user.me(), (cur) =>
          cur && { ...cur, learn_from_edits: context.previous?.learn_from_edits ?? cur.learn_from_edits },
        )
    },
    onSuccess: (_data, next) => {
      emit('learn_from_edits_changed', { learn_from_edits_enabled: next })
    },
    onSettled: () => client.invalidateQueries({ queryKey: keys.user.me() }),
  })
}
